import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronUp, ChevronDown, Filter, Search, MoreVertical,ChevronLeft,ChevronRight,X,Plus,Download,Eye,EyeOff,ArrowUpDown,SortAsc,SortDesc,Settings,Columns3,FileText,Grid3x3,Pin,PinOff,GripVertical, EyeIcon, Edit} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const EmployeeTableDataGrid = ({rawData, columnOrderData, baseColumnsData, hiddenColumnsData=new Set([]), filteredDataCount}) => {
  const {user}=useAuth();
  const [data, setData] = useState(rawData);
  const [loading, setLoading] = useState(false);
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [hiddenColumns, setHiddenColumns] = useState(hiddenColumnsData);
  const [showColumnPanel, setShowColumnPanel] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [pinnedColumns, setPinnedColumns] = useState({ left: [], right: [] });
  
  // NEW: Expandable rows state
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Drag and drop states
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [draggedFilter, setDraggedFilter] = useState(null);
  const [columnOrder, setColumnOrder] = useState(columnOrderData);
  const [isDraggingColumn, setIsDraggingColumn] = useState(false);
  const [isDraggingFilter, setIsDraggingFilter] = useState(false);
  const navigate = useNavigate();

  const exportMenuRef = useRef(null);
  const columnMenuRef = useRef(null);
  const columnPanelRef = useRef(null);

  const [baseColumns, setBaseColumns] = useState(baseColumnsData);

  useEffect(() => {
    setData(rawData);
  }, [rawData]);

  // NEW: Toggle row expansion
  const toggleRow = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  // NEW: Function to check if a value is numeric
  const isNumeric = (value) => {
    return !isNaN(parseFloat(value)) && isFinite(value);
  };

  // NEW: Function to parse logical filter expressions
  const parseLogicalFilter = (filterValue, fieldValue) => {
    if (!filterValue || filterValue.trim() === '') return true;
    
    const value = filterValue.trim();
    
    // Check for range patterns like "18>=40", "20<=50", "10>=30", etc.
    const rangePatterns = [
      /^(\d+(?:\.\d+)?)\s*>=\s*(\d+(?:\.\d+)?)$/,  // min>=max
      /^(\d+(?:\.\d+)?)\s*<=\s*(\d+(?:\.\d+)?)$/,  // min<=max
      /^(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)$/,   // min-max
      /^(\d+(?:\.\d+)?)\s*to\s*(\d+(?:\.\d+)?)$/i, // min to max
    ];
    
    // Check for comparison patterns like ">18", "<=40", "!=25", etc.
    const comparisonPatterns = [
      { regex: /^>=\s*(\d+(?:\.\d+)?)$/, operator: '>=' },
      { regex: /^<=\s*(\d+(?:\.\d+)?)$/, operator: '<=' },
      { regex: /^>\s*(\d+(?:\.\d+)?)$/, operator: '>' },
      { regex: /^<\s*(\d+(?:\.\d+)?)$/, operator: '<' },
      { regex: /^!=\s*(\d+(?:\.\d+)?)$/, operator: '!=' },
      { regex: /^=\s*(\d+(?:\.\d+)?)$/, operator: '=' },
    ];
    
    const numericFieldValue = parseFloat(fieldValue);
    
    // If field value is not numeric, fall back to contains
    if (!isNumeric(fieldValue)) {
      return fieldValue.toString().toLowerCase().includes(value.toLowerCase());
    }
    
    // Check for range patterns
    for (const pattern of rangePatterns) {
      const match = value.match(pattern);
      if (match) {
        const min = parseFloat(match[1]);
        const max = parseFloat(match[2]);
        return numericFieldValue >= min && numericFieldValue <= max;
      }
    }
    
    // Check for comparison patterns
    for (const { regex, operator } of comparisonPatterns) {
      const match = value.match(regex);
      if (match) {
        const compareValue = parseFloat(match[1]);
        switch (operator) {
          case '>=':
            return numericFieldValue >= compareValue;
          case '<=':
            return numericFieldValue <= compareValue;
          case '>':
            return numericFieldValue > compareValue;
          case '<':
            return numericFieldValue < compareValue;
          case '!=':
            return numericFieldValue !== compareValue;
          case '=':
            return numericFieldValue === compareValue;
          default:
            return true;
        }
      }
    }
    
    // If no special pattern is found and it's numeric, try exact match
    if (isNumeric(value)) {
      return numericFieldValue === parseFloat(value);
    }
    
    // Fall back to contains for text
    return fieldValue.toString().toLowerCase().includes(value.toLowerCase());
  };

  // Get columns in the correct order
  const columns = useMemo(() => {
    return columnOrder.map(fieldName => 
      baseColumns.find(col => col.field === fieldName)
    ).filter(Boolean);
  }, [columnOrder]);

  const visibleColumns = columns.filter(col => !hiddenColumns.has(col.field));
  
  // Organize columns by pinning
  const organizedColumns = useMemo(() => {
    const leftPinned = pinnedColumns.left.map(field => columns.find(col => col.field === field)).filter(Boolean);
    const rightPinned = pinnedColumns.right.map(field => columns.find(col => col.field === field)).filter(Boolean);
    const unpinned = visibleColumns.filter(col => 
      !pinnedColumns.left.includes(col.field) && 
      !pinnedColumns.right.includes(col.field)
    );
    
    return [...leftPinned, ...unpinned, ...rightPinned];
  }, [visibleColumns, pinnedColumns, columns]);

  // Close menus on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
        setColumnMenuAnchor(null);
        setSelectedColumn(null);
      }
      if (columnPanelRef.current && !columnPanelRef.current.contains(event.target)) {
        setShowColumnPanel(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Column drag handlers
  const handleColumnDragStart = (e, column) => {
    setDraggedColumn(column);
    setIsDraggingColumn(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
  };

  const handleColumnDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleColumnDrop = (e, targetColumn) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn.field === targetColumn.field) return;

    const sourceIndex = columnOrder.indexOf(draggedColumn.field);
    const targetIndex = columnOrder.indexOf(targetColumn.field);
    
    const newOrder = [...columnOrder];
    newOrder.splice(sourceIndex, 1);
    newOrder.splice(targetIndex, 0, draggedColumn.field);
    
    setColumnOrder(newOrder);
    setDraggedColumn(null);
    setIsDraggingColumn(false);
  };

  const handleColumnDragEnd = () => {
    setDraggedColumn(null);
    setIsDraggingColumn(false);
  };

  // Filter drag handlers
  const handleFilterDragStart = (e, filterId) => {
    setDraggedFilter(filterId);
    setIsDraggingFilter(true);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleFilterDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleFilterDrop = (e, targetFilterId) => {
    e.preventDefault();
    if (!draggedFilter || draggedFilter === targetFilterId) return;

    const sourceIndex = filterModel.items.findIndex(item => item.id === draggedFilter);
    const targetIndex = filterModel.items.findIndex(item => item.id === targetFilterId);
    
    const newItems = [...filterModel.items];
    const [draggedItem] = newItems.splice(sourceIndex, 1);
    newItems.splice(targetIndex, 0, draggedItem);
    
    setFilterModel({ items: newItems });
    setDraggedFilter(null);
    setIsDraggingFilter(false);
  };

  const handleFilterDragEnd = () => {
    setDraggedFilter(null);
    setIsDraggingFilter(false);
  };

  // Sorting logic
  const handleSort = (field) => {
    const existingSort = sortModel.find(sort => sort.field === field);
    let newSortModel = [];
    
    if (!existingSort) {
      newSortModel = [{ field, sort: 'asc' }];
    } else if (existingSort.sort === 'asc') {
      newSortModel = [{ field, sort: 'desc' }];
    } else {
      newSortModel = [];
    }
    
    setSortModel(newSortModel);
  };

  // Column menu handlers
  const handleColumnMenuClick = (event, column) => {
    event.stopPropagation();
    setColumnMenuAnchor(columnMenuAnchor === event.currentTarget ? null : event.currentTarget);
    setSelectedColumn(column);
  };

  const handleSortAsc = () => {
    setSortModel([{ field: selectedColumn.field, sort: 'asc' }]);
    setColumnMenuAnchor(null);
  };

  const handleSortDesc = () => {
    setSortModel([{ field: selectedColumn.field, sort: 'desc' }]);
    setColumnMenuAnchor(null);
  };

  const handleUnsort = () => {
    setSortModel([]);
    setColumnMenuAnchor(null);
  };

  const handleHideColumn = () => {
    setHiddenColumns(prev => new Set([...prev, selectedColumn.field]));
    setColumnMenuAnchor(null);
  };

  const handleShowColumn = (field) => {
    setHiddenColumns(prev => {
      const newSet = new Set(prev);
      newSet.delete(field);
      return newSet;
    });
  };

  const handleShowAllColumns = () => {
    setHiddenColumns(new Set());
  };

  const handleHideAllColumns = () => {
    setHiddenColumns(new Set(columns.map(col => col.field)));
  };

  const handleResetColumns = () => {
    setHiddenColumns(hiddenColumnsData);
    setPinnedColumns({ left: [], right: [] });
    setSortModel([]);
    setFilterModel({ items: [] });
    setColumnOrder(columnOrderData);
    setExpandedRows(new Set()); // Reset expanded rows too
  };

  const handlePinLeft = () => {
    setPinnedColumns(prev => ({
      ...prev,
      left: [...prev.left.filter(f => f !== selectedColumn.field), selectedColumn.field],
      right: prev.right.filter(f => f !== selectedColumn.field)
    }));
    setColumnMenuAnchor(null);
  };

  const handlePinRight = () => {
    setPinnedColumns(prev => ({
      ...prev,
      right: [...prev.right.filter(f => f !== selectedColumn.field), selectedColumn.field],
      left: prev.left.filter(f => f !== selectedColumn.field)
    }));
    setColumnMenuAnchor(null);
  };

  const handleUnpin = () => {
    setPinnedColumns(prev => ({
      left: prev.left.filter(f => f !== selectedColumn.field),
      right: prev.right.filter(f => f !== selectedColumn.field)
    }));
    setColumnMenuAnchor(null);
  };

  const handleFilterColumn = () => {
    const existingFilter = filterModel.items.find(item => item.field === selectedColumn.field);
    if (!existingFilter) {
      setFilterModel(prev => ({
        items: [...prev.items, { field: selectedColumn.field, operator: 'logical', value: '', id: Date.now() }]
      }));
      setShowFilterPanel(true);
    }
    setColumnMenuAnchor(null);
  };

  // Export functions
  const exportToCSV = () => {
    const csvContent = [
      visibleColumns.map(col => col.headerName).join(','),
      ...filteredAndSortedData.map(row => 
        visibleColumns.map(col => {
          const value = row[col.field];
          return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'data-export.csv';
    link.click();
    setShowExportMenu(false);
  };

  const exportToJSON = () => {
    const jsonContent = JSON.stringify(filteredAndSortedData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'data-export.json';
    link.click();
    setShowExportMenu(false);
  };

  // Filter logic
  const addFilter = () => {
    setFilterModel(prev => ({
      items: [...prev.items, { field: columnOrderData[0] || '', operator: 'logical', value: '', id: Date.now() }]
    }));
  };

  const updateFilter = (id, key, value) => {
    setFilterModel(prev => ({
      items: prev.items.map(item => 
        item.id === id ? { ...item, [key]: value } : item
      )
    }));
  };

  const removeFilter = (id) => {
    setFilterModel(prev => ({
      items: prev.items.filter(item => item.id !== id)
    }));
  };

  // Apply filters and sorting - UPDATED with logical filter support
  const filteredAndSortedData = useMemo(() => {
    const search = searchTerm?.toLowerCase() || "";
  
    const filtered = data.filter(row => {
      // 🔍 Apply search only on Employee ID and Name
      if (search) {
        const employeeId = row["Employee ID"]?.toString().toLowerCase() || "";
        const employeeName = row["Employee Name"]?.toString().toLowerCase() || "";
        if (!(employeeId.includes(search) || employeeName.includes(search))) {
          return false;
        }
      }
  
      // 🧩 Apply filters
      for (const filter of filterModel.items) {
        if (!filter.value) continue;
  
        const fieldValue = row[filter.field];
        const filterValue = filter.value;
  
        if (filter.operator === "logical") {
          if (!parseLogicalFilter(filterValue, fieldValue)) return false;
        } else {
          const fieldValueStr = fieldValue?.toString().toLowerCase() || "";
          const filterValueStr = filterValue.toString().toLowerCase();
  
          switch (filter.operator) {
            case "contains":
              if (!fieldValueStr.includes(filterValueStr)) return false;
              break;
            case "equals":
              if (fieldValueStr !== filterValueStr) return false;
              break;
            case "startsWith":
              if (!fieldValueStr.startsWith(filterValueStr)) return false;
              break;
            case "endsWith":
              if (!fieldValueStr.endsWith(filterValueStr)) return false;
              break;
            case "isEmpty":
              if (fieldValueStr !== "") return false;
              break;
            case "isNotEmpty":
              if (fieldValueStr === "") return false;
              break;
            default:
              break;
          }
        }
      }
  
      return true;
    });
  
    // ↕️ Apply sorting
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      filtered.sort((a, b) => {
        const aVal = a[field] ?? "";
        const bVal = b[field] ?? "";
  
        if (typeof aVal === "string" && typeof bVal === "string") {
          return sort === "asc"
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }
        if (aVal < bVal) return sort === "asc" ? -1 : 1;
        if (aVal > bVal) return sort === "asc" ? 1 : -1;
        return 0;
      });
    }
  
    return filtered;
  }, [data, filterModel, sortModel, searchTerm]);
  

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedData.length / pageSize);
  const paginatedData = filteredAndSortedData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  useEffect(() => {
    filteredDataCount(filteredAndSortedData.length);
  }, [filteredAndSortedData]);

  const renderCell = (row, column) => {
    const value = row[column.field];
    if (column.field === 'isAdmin') {
      return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
        }`}>
          {value ? 'Yes' : 'No'}
        </span>
      );
    }
    if (column.field === 'rating') {
      return (
        <div className="flex items-center space-x-2">
          <span className="font-medium">{value}</span>
          <div className="flex">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full mr-0.5 ${
                  i < Math.floor(value) ? 'bg-yellow-400' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>
      );
    }
    if (column.field === 'salary') {
      return <span className="font-medium">${value?.toLocaleString()}</span>;
    }
    return <span className="truncate">{value?.toLocaleString()}</span>;
  };

  const getSortIcon = (column) => {
    const sort = sortModel.find(s => s.field === column.field);
    if (!sort) return <ArrowUpDown className="w-3 h-3 text-gray-400" />;
    return sort.sort === 'asc' ? 
      <SortAsc className="w-3 h-3 text-blue-600" /> : 
      <SortDesc className="w-3 h-3 text-blue-600" />;
  };

  // NEW: Render expanded row details
  const sectionMapping = {
    "Personal Information": [
      "Employee Name",
      "Father Name",
      "Mother Name (Family Details)",
      "Date of Birth",
      "Gender",
      "Marital Status",
      "Blood Group",
      "Nationality",
      "Religion",
      "Languages Known"
    ],
    "Address Information": [
      "Address - House",
      "Address - Street",
      "Address - Landmark",
      "Address - Village",
      "Address - District",
      "Address - State",
      "Address - Post"
    ],
    "Contact Information": [
      "Phone Number",
      "Personal E-mail",
      "Official E-mail",
      "Emergency Contact Name",
      "Emergency Contact Mobile",
      "Emergency Contact Email"
    ],
    "Identification Documents": [
      "Aadhaar",
      "PAN",
      "UAN"
    ],
    "Employment Details": [
      "Designation",
      "Grade",
      "Department",
      "Division",
      "Location",
      "Last Organization",
      "Reporting Manager",
      "Reporting Manager Designation",
      "Reporting Manager Employee ID",
      "Source of Hiring",
      "Date of Joining",
      "Date of Offer",
      "Last Promotion Date",
      "Total Experience"
    ],
    "Salary and Compensation": [
      "At_joining CTC",
      "At_joining Basic",
      "At_joining Gross",
      "At_joining Incentive",
      "2022-23 CTC",
      "2022-23 Basic",
      "2022-23 Gross",
      "2022-23 Incentive",
      "2023-24 CTC",
      "2023-24 Basic",
      "2023-24 Gross",
      "2023-24 Incentive",
      "2024-25 CTC",
      "2024-25 Basic",
      "2024-25 Gross",
      "2024-25 Incentive",
      "2025-26 CTC",
      "2025-26 Basic",
      "2025-26 Gross",
      "2025-26 Incentive"
    ],
    "Declarations and Compliance": [
      "Physical Disabilities",
      "Major Illness",
      "Court Case(s)",
      "Police Custody"
    ],
    "Education": [
      "Tenth",
      "Twelfth",
      "Graduation",
      "Post_graduation",
      "Professional",
      "Other Qualification"
    ],
    "Bank Details": [
      "Bank Name",
      "Bank Account Number",
      "IFSC"
    ]
  };
  
  const renderSection = (row, sectionName, fields) => (
    <div key={sectionName} className="bg-gray-50 border rounded-lg p-4 shadow-sm">
      <h5 className="font-semibold text-gray-700 mb-3 uppercase">{sectionName}</h5>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {fields.map((key) => (
          <div key={key} className="border-l-4 border-blue-400 pl-3">
            <dt className="text-sm font-medium text-gray-600">{key}:</dt>
            <dd className="text-sm text-gray-900 break-words">{row[key] || ""}</dd>
          </div>
        ))}
      </div>
    </div>
  );
  
  const renderExpandedRow = (row) => (
    <tr className="bg-blue-50">
      <td colSpan={organizedColumns.length + 1} className="px-4 py-4">
        <div className="bg-white rounded-lg p-4 shadow-inner">
          <h4 className="font-semibold text-gray-800 mb-3">Complete Data:</h4>
          <div className="space-y-6">
            {Object.entries(sectionMapping).map(([sectionName, fields]) =>
              renderSection(row, sectionName, fields)
            )}
          </div>
        </div>
      </td>
    </tr>
  );
  
  

  if (loading) {
    return (
      <div className="h-96 w-full border border-gray-300 rounded bg-white">
        {/* Loading Toolbar */}
        <div className="h-14 border-b border-gray-200 bg-white flex items-center justify-between px-4">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
            <div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
            <div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
        </div>
        
        {/* Loading Table */}
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="text-sm text-gray-500">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(80vh)] w-full border border-gray-300 rounded bg-white flex flex-col">
      {/* Toolbar */}
      <div className="h-14 border-b border-gray-200 bg-white flex items-center justify-between px-4 flex-shrink-0">
        <div className="flex items-center space-x-1">
          {/* Column visibility */}
          <div className="relative" ref={columnPanelRef}>
            <button
              onClick={() => setShowColumnPanel(!showColumnPanel)}
              className="p-2 hover:bg-gray-100 rounded transition-colors"
              title="Show/Hide columns"
            >
              <Columns3 className="w-4 h-4 text-gray-600" />
            </button>
            
            {showColumnPanel && (
              <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-[51] py-2">
                <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase border-b border-gray-100">
                  Show columns
                </div>
                
                {/* Show/Hide All and Reset buttons */}
                <div className="px-3 py-2 border-b border-gray-100 space-y-1">
                  <div className="flex space-x-2">
                    <button
                      onClick={handleShowAllColumns}
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      Show all
                    </button>
                    <span className="text-xs text-gray-300">|</span>
                    <button
                      onClick={handleHideAllColumns}
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      Hide all
                    </button>
                    <span className="text-xs text-gray-300">|</span>
                    <button
                      onClick={handleResetColumns}
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      Reset
                    </button>
                  </div>
                </div>
                
                {/* Column list - Draggable */}
                <div className="max-h-60 overflow-y-auto">
                  {columns.map((column, index) => (
                    <div 
                      key={column.field} 
                      className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer group"
                      draggable
                      onDragStart={(e) => handleColumnDragStart(e, column)}
                      onDragOver={handleColumnDragOver}
                      onDrop={(e) => handleColumnDrop(e, column)}
                      onDragEnd={handleColumnDragEnd}
                    >
                      <GripVertical className="w-3 h-3 text-gray-400 mr-2 opacity-0 group-hover:opacity-100 cursor-move" />
                      <input
                        type="checkbox"
                        checked={!hiddenColumns.has(column.field)}
                        onChange={() => {
                          if (hiddenColumns.has(column.field)) {
                            handleShowColumn(column.field);
                          } else {
                            setHiddenColumns(prev => new Set([...prev, column.field]));
                          }
                        }}
                        className="mr-2 rounded"
                      />
                      <span className="text-sm flex-1">{column.headerName}</span>
                      <div className="flex space-x-1">
                        {pinnedColumns.left.includes(column.field) && (
                          <span className="text-xs text-blue-600 bg-blue-100 px-1 rounded">L</span>
                        )}
                        {pinnedColumns.right.includes(column.field) && (
                          <span className="text-xs text-green-600 bg-green-100 px-1 rounded">R</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Filters */}
          <button
            onClick={() => setShowFilterPanel(!showFilterPanel)}
            className={`p-2 hover:bg-gray-100 rounded transition-colors ${
              filterModel.items.length > 0 ? 'text-blue-600' : 'text-gray-600'
            }`}
            title="Show filters"
          >
            <Filter className="w-4 h-4" />
          </button>

          {/* Export */}
          <div className="relative" ref={exportMenuRef}>
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="p-2 hover:bg-gray-100 rounded transition-colors"
              title="Export"
            >
              <Download className="w-4 h-4 text-gray-600" />
            </button>
            
            {showExportMenu && (
              <div className="absolute top-full left-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-[51] py-1">
                <button
                  onClick={exportToCSV}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
                >
                  <FileText className="w-4 h-4" />
                  <span>Export CSV</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right side toolbar */}
        <div className="flex items-center space-x-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 pr-3 py-1 sm:w-52 w-full text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Filter Panel - Enhanced with logical filters */}
      {showFilterPanel && (
        <div className="border-b border-gray-200 bg-blue-50 p-4 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-medium text-gray-900 text-sm">Filters (Drag to reorder)</h3>
              <p className="text-xs text-gray-600 mt-1">
                For numeric fields: Use ranges like "18{'>'}=40", comparisons like "{'>'}25", "{'<'}=100", "{'!'}=30"
              </p>
            </div>
            <button
              onClick={() => setShowFilterPanel(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {filterModel.items.map((filter, index) => (
            <div 
              key={filter.id} 
              className={`flex items-center space-x-2 mb-2 p-2 rounded transition-colors ${
                draggedFilter === filter.id ? 'bg-blue-100 opacity-50' : 'bg-white hover:bg-gray-50'
              }`}
              draggable
              onDragStart={(e) => handleFilterDragStart(e, filter.id)}
              onDragOver={handleFilterDragOver}
              onDrop={(e) => handleFilterDrop(e, filter.id)}
              onDragEnd={handleFilterDragEnd}
            >
              <GripVertical className="w-4 h-4 text-gray-400 cursor-move flex-shrink-0" />
              
              <select
                value={filter.field}
                onChange={(e) => updateFilter(filter.id, 'field', e.target.value)}
                className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {columns.filter(col => col.filterable).map(col => (
                  <option key={col.field} value={col.field}>{col.headerName}</option>
                ))}
              </select>
              
              <select
                value={filter.operator}
                onChange={(e) => updateFilter(filter.id, 'operator', e.target.value)}
                className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="contains">contains</option>
                <option value="equals">equals</option>
                <option value="startsWith">starts with</option>
                <option value="endsWith">ends with</option>
                <option value="isEmpty">is empty</option>
                <option value="isNotEmpty">is not empty</option>
                <option value="logical">Smart Filter</option>
              </select>
              
              <input
                type="text"
                value={filter.value}
                onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                placeholder={filter.operator === 'logical' ? "e.g., 18>=40, >25, <=100, text..." : "Filter value"}
                className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1"
              />
              
              <button
                onClick={() => removeFilter(filter.id)}
                className="text-red-500 hover:text-red-700 p-1 flex-shrink-0"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          
          <button
            onClick={addFilter}
            className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-xs"
          >
            <Plus className="w-3 h-3" />
            <span>Add filter</span>
          </button>
        </div>
      )}

      {/* Table Container */}
      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0" style={{zIndex: 1}}>
            <tr className="border-b border-gray-200">
              {user?.right==='ADMIN'&&<th className="w-12 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky left-0">
                Edit
              </th>}
              {/* NEW: Expand column */}
              <th className="w-12 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky left-0">
                View Details
              </th>
              <th className="w-12 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 sticky left-0" style={{zIndex: 2}}>
                Document Access
              </th>
              {organizedColumns.map((column, index) => {
                const isPinnedLeft = pinnedColumns.left.includes(column.field);
                const isPinnedRight = pinnedColumns.right.includes(column.field);
                const isDragged = draggedColumn?.field === column.field;
                
                return (
                  <th
                    key={column.field}
                    draggable={!isPinnedLeft && !isPinnedRight}
                    onDragStart={(e) => handleColumnDragStart(e, column)}
                    onDragOver={handleColumnDragOver}
                    onDrop={(e) => handleColumnDrop(e, column)}
                    onDragEnd={handleColumnDragEnd}
                    className={`text-center text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-200 last:border-r-0 relative group select-none transition-all z-1 ${
                      isPinnedLeft ? 'bg-blue-50 sticky left-12 z-20' : ''
                    } ${
                      isPinnedRight ? 'bg-green-50 sticky right-0 z-20' : ''
                    } ${
                      isDragged ? 'opacity-50 bg-blue-100' : ''
                    } ${
                      !isPinnedLeft && !isPinnedRight ? 'cursor-move hover:bg-gray-100' : ''
                    }`}
                    style={{ width: column.width, minWidth: column.width }}
                  >
                    <div className="flex items-center justify-between h-10 px-3">
                      <div className="flex items-center space-x-1 flex-1 min-w-0">
                        {!isPinnedLeft && !isPinnedRight && (
                          <GripVertical className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 mr-1" />
                        )}
                        <span className="truncate">{column.headerName}</span>
                        {column.sortable && (
                          <button
                            onClick={() => handleSort(column.field)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            {getSortIcon(column)}
                          </button>
                        )}
                      </div>
                      <button
                        onClick={(e) => handleColumnMenuClick(e, column)}
                        className="opacity-0 group-hover:opacity-100 hover:bg-gray-200 p-1 rounded transition-opacity ml-1"
                      >
                        <MoreVertical className="w-3 h-3" />
                      </button>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="bg-white">
            {paginatedData.map((row, index) => (
              <React.Fragment key={row["Employee ID"]}>
                <tr className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                {user?.right==='ADMIN'&&<td className="w-12 px-3 py-2 text-center bg-gray-50 sticky left-0">
                  <button
                    onClick={() => navigate(`/employee-edit/${row["Employee ID"]}`)}
                    className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded hover:bg-gray-100 z-20"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </td>}
                  {/* NEW: Expand button column */}
                  <td className="w-12 px-3 py-2 text-center bg-gray-50 sticky left-0">
                    <button
                      onClick={() => toggleRow(row["Employee ID"])}
                      className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded hover:bg-gray-100 z-20"
                    >
                      {expandedRows.has(row["Employee ID"]) ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>
                  </td>
                  <td className="w-12 px-3 py-2 text-center bg-gray-50 sticky left-0">
                    <button
                      className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded hover:bg-gray-100 z-20"
                      onClick={() => navigate(`/employee-document-access/${row["Employee ID"]}`)}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                  {organizedColumns.map((column) => {
                    const isPinnedLeft = pinnedColumns.left.includes(column.field);
                    const isPinnedRight = pinnedColumns.right.includes(column.field);
                    
                    return (
                      <td
                        key={column.field}
                        className={`px-3 py-2 text-sm text-center text-gray-900 border-r border-gray-100 last:border-r-0 ${
                          isPinnedLeft ? 'bg-blue-50/50 sticky left-12 z-10' : ''
                        } ${
                          isPinnedRight ? 'bg-green-50/50 sticky right-0 z-10' : ''
                        }`}
                        style={{ width: column.width, minWidth: column.width, maxWidth: column.width }}
                      >
                        {renderCell(row, column)}
                      </td>
                    );
                  })}
                </tr>
                {/* NEW: Expanded row */}
                {expandedRows.has(row["Employee ID"]) && renderExpandedRow(row)}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Column Menu */}
      {columnMenuAnchor && selectedColumn && (
        <div
          ref={columnMenuRef}
          className="fixed bg-white border border-gray-200 rounded-lg shadow-lg z-30 py-1 w-44"
          style={{
            top: columnMenuAnchor.getBoundingClientRect().bottom + 4,
            left: columnMenuAnchor.getBoundingClientRect().left,
          }}
        >
          {/* Sorting options */}
          <button
            onClick={handleSortAsc}
            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
          >
            <SortAsc className="w-4 h-4" />
            <span>Sort ASC</span>
          </button>
          <button
            onClick={handleSortDesc}
            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
          >
            <SortDesc className="w-4 h-4" />
            <span>Sort DESC</span>
          </button>
          <button
            onClick={handleUnsort}
            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
          >
            <ArrowUpDown className="w-4 h-4" />
            <span>Unsort</span>
          </button>
          
          <div className="border-t border-gray-100 my-1"></div>
          
          {/* Pin options */}
          {!pinnedColumns.left.includes(selectedColumn.field) && (
            <button
              onClick={handlePinLeft}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
            >
              <Pin className="w-4 h-4" />
              <span>Pin to left</span>
            </button>
          )}
          
          {!pinnedColumns.right.includes(selectedColumn.field) && (
            <button
              onClick={handlePinRight}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
            >
              <Pin className="w-4 h-4 rotate-180" />
              <span>Pin to right</span>
            </button>
          )}
          
          {(pinnedColumns.left.includes(selectedColumn.field) || pinnedColumns.right.includes(selectedColumn.field)) && (
            <button
              onClick={handleUnpin}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
            >
              <PinOff className="w-4 h-4" />
              <span>Unpin</span>
            </button>
          )}
          
          <div className="border-t border-gray-100 my-1"></div>
          
          {/* Filter and visibility options */}
          <button
            onClick={handleFilterColumn}
            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
          >
            <Filter className="w-4 h-4" />
            <span>Add logical filter</span>
          </button>
          
          <button
            onClick={handleHideColumn}
            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
          >
            <EyeOff className="w-4 h-4" />
            <span>Hide column</span>
          </button>
          
          <div className="border-t border-gray-100 my-1"></div>
          
          <button
            onClick={() => {
              setShowColumnPanel(true);
              setColumnMenuAnchor(null);
            }}
            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
          >
            <Settings className="w-4 h-4" />
            <span>Manage columns</span>
          </button>
        </div>
      )}

      {/* Pagination */}
      <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-shrink-0">
        {/* Rows per page selector */}
        <div className="flex items-center justify-center sm:justify-start space-x-2">
          <span className="text-sm text-gray-700">Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        {/* Page info + pagination */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 justify-center sm:justify-end">
          {/* Page info */}
          <span className="text-sm text-gray-700 text-center sm:text-left">
            {((currentPage - 1) * pageSize) + 1}–
            {Math.min(currentPage * pageSize, filteredAndSortedData.length)} of{" "}
            {filteredAndSortedData.length}
          </span>

          {/* Pagination buttons */}
          <div className="flex items-center justify-center flex-wrap gap-1">
            {/* First page */}
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="p-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 text-sm"
              title="First page"
            >
              <ChevronLeft className="w-4 h-4" />
              <ChevronLeft className="w-4 h-4 -ml-2" />
            </button>

            {/* Previous page */}
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="p-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              title="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {/* Next page */}
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="p-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              title="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>

            {/* Last page */}
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="p-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 text-sm"
              title="Last page"
            >
              <ChevronRight className="w-4 h-4" />
              <ChevronRight className="w-4 h-4 -ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeTableDataGrid;